/**
 * Pricing model for the blood-test membership experiment.
 *
 * Two ways to pay for any booking that contains a blood component:
 *   - "yearly": become a member now — every item is charged at its Stripe
 *     member price, and the blood test itself becomes the CHF 299/year
 *     membership subscription (billed yearly instead of once).
 *   - "once": pay the full (non-member) price later, at the clinic.
 *
 * The standalone advanced blood test IS the membership, so it never receives
 * a one-time discount — its contribution equals the yearly membership price.
 */
import {
  CADENCE_FOLLOWUPS,
  FOLLOWUP_PRICE_CHF,
  type RecommendedPlan,
} from "@/utils/membershipPricing";

// Structural minimum this module actually reads — accepts both the rich
// product-step `Product` and the leaner order-summary `Product` (neither's
// `description` is touched here, so we don't require it).
type Product = {
  stripe_product_id?: string;
  stripe_metadata_id?: string | null;
  includes_blood_draw?: boolean;
  // ['mri'], ['blood-only'], or ['mri','blood-only'] — an 'mri' entry (without
  // 'blood-only') marks a scan package, which can carry a membership (the bundle).
  package_types?: string[] | null;
  default_price?: { unit_amount?: number | null } | null;
  member_price?: { unit_amount?: number | null } | null;
};

/** Yearly membership price in cents (CHF 299/year). */
export const YEARLY_MEMBERSHIP_CENTS = 29900;

/**
 * The standalone advanced blood test — the main blood product that BECOMES the
 * yearly membership when the user opts in.
 *
 * Identified structurally (package type + blood draw, all DB columns), NEVER by
 * a Stripe product id. The live and sandbox Stripe accounts use different
 * product ids, so any id-based gate silently disabled membership on every
 * non-prod deployment; the DB `package_types` column is identical everywhere.
 *
 * Note `package_types` lists the *flows a product appears in*, not its kind:
 * add-on blood tests carry BOTH 'blood-only' and 'mri', while the standalone
 * blood package is 'blood-only' only. So "blood-only AND not mri" uniquely
 * picks out the standalone test, even from a mixed cart.
 *
 * The structural test alone is NOT enough, because the advanced blood test is
 * two rows, not one: `blood-test-only` (the package, 'blood-only') and
 * `blood-test-product-nov-25` (the add-on offered inside the MRI flow, so
 * 'blood-only' + 'mri'). Both map to the SAME Stripe product, and the checkout
 * edge function dedupes on that — so the add-on row is the same membership,
 * and quoting it as a separate CHF 279 line produced a total the charge could
 * never contain (the "one-time total mismatch" 409, i.e. checkout refused
 * outright on a membership + Core cart). The shared
 * `stripe_metadata_id = 'blood-test-only'` is the canonical "this row IS the
 * advanced blood test" signal (mode-agnostic, identical live + sandbox) and
 * catches both rows.
 *
 * Deliberately NOT a widened package_types check: 'blood-only' + 'mri' +
 * includes_blood_draw is shared with genuinely separate add-ons (hormone
 * panels, PSA, LPA, cortisol, omega-3), which must keep selling.
 */
export function isBloodMembershipBaseProduct(p: Product): boolean {
  if (p.stripe_metadata_id === "blood-test-only") return true;
  const types = p.package_types ?? [];
  return (
    types.includes("blood-only") &&
    !types.includes("mri") &&
    p.includes_blood_draw === true
  );
}

/**
 * A main scan / MRI package (Core, Advanced, Pro). These are typed 'mri' and
 * not 'blood-only'. A scan in the cart makes it a candidate for the membership
 * bundle (see isBundleCart) — member-priced, with the blood-bearing scans netted.
 */
export function isScanPackage(p: Product): boolean {
  const types = p.package_types ?? [];
  return types.includes("mri") && !types.includes("blood-only");
}

/**
 * The follow-up blood test. Structurally it looks like the membership base
 * (blood-only + blood draw), but the membership is built around the ANNUAL
 * baseline test — a follow-up can't anchor it. Identified by its mode-agnostic
 * `stripe_metadata_id` (identical in live + sandbox), never a product id.
 */
function isFollowupBloodTest(p: Product): boolean {
  return p.stripe_metadata_id === "followup-blood-test";
}

/**
 * Full (non-member) price contribution of a product, in cents.
 * The standalone blood test IS the membership, so it's priced at the selected
 * cadence's annual price (`membershipCents`) — not a flat amount. Falls back to
 * the once-tier default when no cadence price is supplied (non-cadence path).
 */
export function fullContributionCents(
  p: Product,
  membershipCents?: number,
): number {
  if (isBloodMembershipBaseProduct(p)) {
    return membershipCents ?? YEARLY_MEMBERSHIP_CENTS;
  }
  return p.default_price?.unit_amount ?? 0;
}

/**
 * Member-price contribution of a product, in cents.
 * The blood test is the yearly membership itself (priced at the cadence), so it
 * never gets a separate one-time member discount.
 */
export function memberContributionCents(
  p: Product,
  membershipCents?: number,
): number {
  if (isBloodMembershipBaseProduct(p)) {
    return membershipCents ?? YEARLY_MEMBERSHIP_CENTS;
  }
  return p.member_price?.unit_amount ?? p.default_price?.unit_amount ?? 0;
}

export interface BloodMembershipPricing {
  fullTotalCents: number;
  memberTotalCents: number;
  savingsCents: number;
  /**
   * The individual member-priced amounts Stripe bills on the signup invoice —
   * each cart item at its member contribution (the base test at the cadence
   * price). A percentage coupon is applied and rounded PER LINE by Stripe, so
   * any surface quoting a discounted member total has to compute it over
   * these, not over memberTotalCents.
   */
  memberLines: number[];
}

/**
 * @param membershipCents annual price (cents) of the selected cadence tier; the
 *   blood-test line is charged at this so the order total tracks the cadence
 *   (once 299 / twice 499 / four 899). Omit on the non-membership path.
 */
export function computeBloodMembershipPricing(
  products: Product[],
  membershipCents?: number,
): BloodMembershipPricing {
  const fullTotalCents = products.reduce(
    (sum, p) => sum + fullContributionCents(p, membershipCents),
    0,
  );
  const memberLines = products
    .map((p) => memberContributionCents(p, membershipCents))
    .filter((cents) => cents > 0);
  const memberTotalCents = memberLines.reduce((sum, line) => sum + line, 0);
  return {
    fullTotalCents,
    memberTotalCents,
    savingsCents: Math.max(0, fullTotalCents - memberTotalCents),
    memberLines,
  };
}

/**
 * One-time contribution (cents) of a scan package in a membership bundle.
 *
 * EVERY scan is charged at its member price. Adding the membership is what
 * earns the discount, on Core no less than on Advanced — the product call is
 * only that Core's member price is never ADVERTISED on the packages page
 * (Sven, 2026-08-03). Charging Core at list here instead was the half-measure
 * that made the same basket cost 2'289 bought together and 2'189 bought after
 * joining.
 *
 * Only the NETTING differs by package. A scan that carries its own blood draw
 * (Advanced / Pro) makes the membership's first-year test redundant, so the
 * CHF 299 comes out of the scan and renews next year. Core has no blood draw,
 * so the membership is net-new and billed on top: 1'890 + 299 = 2'189.
 */
export function bundleScanContributionCents(p: Product): number {
  const memberCents =
    p.member_price?.unit_amount ?? p.default_price?.unit_amount ?? 0;
  return absorbsMembershipFee(p)
    ? Math.max(0, memberCents - YEARLY_MEMBERSHIP_CENTS)
    : memberCents;
}

/**
 * Whether the scan's own blood draw makes the membership's first-year test
 * redundant, so the CHF 299 is netted out of the scan instead of billed on top.
 * True for Advanced / Pro, false for Core.
 *
 * This is a statement about NETTING, not about what may be displayed. Both
 * kinds of scan are charged at their member price; the difference is only that
 * Advanced pays 2'290 all-in while Core pays 1'890 + 299.
 *
 * It does double as the gate on the packages-page member-price teaser, for a
 * related but distinct reason: "book as a member and save CHF 100" reads as a
 * cheaper booking, and on Core it isn't one — the 299 still lands on top. So we
 * charge the discount there but never advertise it (Sven, 2026-08-03). Don't
 * reuse this to suppress a member price anywhere it is actually charged.
 */
export function absorbsMembershipFee(p: Product): boolean {
  return isScanPackage(p) && p.includes_blood_draw === true;
}

export interface BundlePricing {
  /** Scan (netted if blood-bearing) + add-ons, all at member price. */
  oneTimeCents: number;
  /** The cadence subscription (29900 / 49900 / 89900). */
  recurringCents: number;
  /** Charged at checkout now: one-time items + the subscription's first invoice. */
  payNowCents: number;
  /**
   * The individual amounts Stripe bills — the netted scan, each member-priced
   * add-on, and the cadence subscription — summing to payNowCents. A percentage
   * coupon is applied and rounded PER LINE by Stripe, so any surface quoting a
   * discounted total has to compute it over these, not over the sum.
   */
  lines: number[];
}

/**
 * Pricing for a membership + scan bundle. `cadenceCents` is the selected tier's
 * annual price. Add-ons are member-priced and never netted.
 */
export function computeBundlePricing(
  scanPackage: Product,
  addOns: Product[],
  cadenceCents: number,
): BundlePricing {
  // The standalone advanced blood test IS the membership — the edge function
  // drops it from one-off items unconditionally, so counting it here quoted a
  // total the charge would never contain (guaranteed 409). Product call
  // (Sven, 2026-08-04): the SKU doesn't apply once the user is becoming a
  // member; the add-ons list hides it in membership carts.
  const addOnLines = addOns
    .filter((a) => !isBloodMembershipBaseProduct(a))
    .map(
      (a) => a.member_price?.unit_amount ?? a.default_price?.unit_amount ?? 0,
    )
    .filter((cents) => cents > 0);
  const addOnsCents = addOnLines.reduce((sum, line) => sum + line, 0);
  const scanCents = bundleScanContributionCents(scanPackage);
  const oneTimeCents = scanCents + addOnsCents;
  return {
    oneTimeCents,
    recurringCents: cadenceCents,
    payNowCents: oneTimeCents + cadenceCents,
    lines: [scanCents, ...addOnLines, cadenceCents].filter((c) => c > 0),
  };
}

/**
 * What the membership actually saves on a bundle cart, in cents. Measured
 * against the till: what the same basket would cost booked separately, minus
 * what we charge today, the subscription included.
 *
 * Not a sum of per-line (list - member) deltas. Those ignore the CHF 299
 * entirely, so they'd read the same whether or not the scan absorbs it —
 * see bundleSeparateTotalCents for which baseline each package gets.
 *
 * "Booked separately" has to include the follow-up tests the cadence buys,
 * priced the way getSeparatePriceChf already prices them on the cadence cards.
 * Without them a 2x cadence reads as no saving at all (list 2'490 against a
 * 2'490 charge) when it actually hands over a CHF 250 test on top of the scan.
 * The annual baseline is deliberately NOT counted: in year one that test is the
 * one inside the scan, which is exactly what the netting takes out.
 *
 * Clamped at zero, so carts the membership makes more expensive show nothing.
 */
export function bundleSavingsCents(
  cart: Product[],
  payNowCents: number,
  plan: RecommendedPlan,
): number {
  return Math.max(0, bundleSeparateTotalCents(cart, plan) - payNowCents);
}

/**
 * What this basket would cost booked separately, in cents — every line at list
 * price, plus the follow-up tests the cadence includes at their standalone
 * price.
 *
 * This is the number to strike through next to the charge, and the one
 * bundleSavingsCents subtracts from. Striking the cart's list price alone goes
 * wrong the moment the cadence adds follow-ups: at 4x it would read "2'490 →
 * 2'890", which looks like the price went up rather than like three extra
 * tests were bought.
 */
export function bundleSeparateTotalCents(
  cart: Product[],
  plan: RecommendedPlan,
): number {
  const listTotalCents = cart.reduce(
    (sum, p) => sum + (p.default_price?.unit_amount ?? 0),
    0,
  );
  // Whether the annual blood test belongs in "booked separately" depends on
  // whether the scan already contains one. Advanced/Pro do, so nobody would buy
  // it twice — their baseline is the scan's list price alone and the bundle's
  // win is the member discount (200). Core contains no blood test, so booking
  // the two separately is a real basket: 1'990 + 299.
  //
  // The BASELINE membership price, never the cadence price. A 4x cadence costs
  // 899 because it buys three follow-ups, and those are already priced into the
  // line below at CHF 250 each — charging 899 here would count them twice.
  const scan = cart.find(isScanPackage);
  const annualBloodTestCents =
    scan && !absorbsMembershipFee(scan) ? YEARLY_MEMBERSHIP_CENTS : 0;
  // The catalog prices follow-ups in whole CHF; everything here is cents.
  return (
    listTotalCents +
    annualBloodTestCents +
    CADENCE_FOLLOWUPS[plan] * FOLLOWUP_PRICE_CHF * 100
  );
}

/**
 * Whether to offer the "Become a member" upsell for a cart.
 *
 * Takes the full cart (main products + add-ons in one list). A scan package
 * (Core/Advanced/Pro) can have a membership attached — the bundle — so a scan
 * cart is eligible regardless of add-ons. This branch's feature; it supersedes
 * dev's interim "scan carts never" rule (AHD-7853), which existed because
 * before bundling a scan cart had nothing membership could price.
 *
 * Disqualifiers are read from stable, account-agnostic signals (never a Stripe
 * product id, which differs between the live and sandbox accounts and silently
 * killed the upsell on every non-prod deployment):
 *
 *   1. Follow-up blood test in the cart → never. Membership renews the annual
 *      baseline test; a standalone follow-up has nothing to anchor a membership.
 *
 * Otherwise the cart is eligible as long as it contains the baseline blood test
 * — including the naked baseline (CHF 299, no add-ons). At baseline cadence the
 * membership costs the same as the one-off (savingsCents 0), so the option
 * leads with what membership adds instead of a saving (memberNoteNoSavings).
 */
export function isMembershipEligible(products: Product[]): boolean {
  // A scan package (Core/Advanced/Pro) can have a membership attached — the
  // bundle. Eligible regardless of add-ons.
  if (products.some(isScanPackage)) return true;

  // Follow-up blood test → never anchors a membership.
  if (products.some(isFollowupBloodTest)) return false;

  // Membership renews the baseline blood test, so it must be in the cart. The
  // naked baseline (299, no add-ons) now qualifies — same price as the one-off,
  // pitched on perks not savings.
  return products.some(isBloodMembershipBaseProduct);
}

/**
 * Whether a cart should be treated as a scan + membership BUNDLE — charged as one
 * create-blood-checkout (netted scan one-time items + the cadence subscription).
 *
 * A scan becomes a bundle when the membership is chosen via EITHER entry point:
 *   - the add-ons upsell card, which sets membership `active`, or
 *   - the details-step path-B "become a member & pay now" toggle, which sets the
 *     cadence to "yearly" for a non-member (it deliberately does NOT set `active`,
 *     since path-B visibility keys off `active` and self-setting it would loop).
 *
 * `useBloodCheckout` already charges the bundle for any scan cart, so every
 * display surface must agree with this predicate or display drifts from charge.
 */
export function isBundleCart(opts: {
  cartHasScan: boolean;
  membershipActive: boolean;
  isMember: boolean;
  paymentMode: string | null;
}): boolean {
  const { cartHasScan, membershipActive, isMember, paymentMode } = opts;
  if (!cartHasScan) return false;
  return membershipActive || (!isMember && paymentMode === "yearly");
}

export interface CheckoutItem {
  stripeProductId: string;
  // Mode-agnostic handle so the edge function can resolve the product in either
  // Stripe account (prod ids differ from sandbox ids; metadata is identical).
  stripeMetadataId?: string;
  includesBloodDraw: boolean;
  isAddOn: boolean;
}

/**
 * Build the item list sent to the create-blood-checkout edge function.
 * `isAddOn` is derived from which list a product came from (main products vs
 * add-ons) — NOT from product_type, since blood add-ons aren't typed "addon".
 * This matters: the edge function only subtracts the bundled blood test (CHF
 * 299) from a main scan package, never from an add-on.
 */
export function buildCheckoutItems(
  products: Product[],
  addOns: Product[] = [],
): CheckoutItem[] {
  const toItem = (p: Product, isAddOn: boolean): CheckoutItem => ({
    stripeProductId: p.stripe_product_id!,
    stripeMetadataId: p.stripe_metadata_id ?? undefined,
    includesBloodDraw: p.includes_blood_draw === true,
    isAddOn,
  });
  return [
    ...products
      .filter((p) => !!p.stripe_product_id)
      .map((p) => toItem(p, false)),
    ...addOns.filter((p) => !!p.stripe_product_id).map((p) => toItem(p, true)),
  ];
}

export interface MemberOptionPricing {
  /**
   * The one-time card's price. ALWAYS the cart's own list total, so it agrees
   * with the order summary.
   *
   * Kept as its own field on purpose: this briefly rendered
   * `bundleSeparateTotalCents` (cart + a separately-bought blood test), so the
   * card read CHF 2'439 beside a sidebar saying CHF 2'140. A shared
   * "fullTotalCents" is what allowed that, so the two totals no longer share a
   * name.
   */
  oneTimeTotalCents: number;
  /** The headline price on the membership card — what checkout will charge. */
  memberTotalCents: number;
  /** Struck-through baseline, or null when the card shows no comparison. */
  strikeThroughCents: number | null;
  /** "Save X" badge amount; 0 hides the badge. */
  savingsCents: number;
  /**
   * The invoice lines behind memberTotalCents (bundle lines or the blood
   * model's member lines). Voucher math on the card must run over these —
   * Stripe rounds a percentage coupon per line, not once on the sum.
   */
  memberLines: number[];
  /** Scan cart: priced by the bundle model rather than the blood one. */
  isBundle: boolean;
  /**
   * The membership ADDS a yearly blood test here rather than discounting the
   * scan, so the total goes up. Drives the card's copy and its comparison: a
   * "save" badge would promise the opposite of what happens.
   */
  addsBloodTest: boolean;
}

/** A bundle add-on's contribution, at member price. The standalone advanced
 *  blood test IS the membership, so it never bills as an add-on. */
export function bundleAddOnCents(p: Product): number {
  if (isBloodMembershipBaseProduct(p)) return 0;
  return p.member_price?.unit_amount ?? p.default_price?.unit_amount ?? 0;
}

/**
 * Which pricing model the details-step membership card should display.
 *
 * This existed only inside the checkout before, and the card showed
 * `computeBloodMembershipPricing` for every cart. That sums member prices and
 * knows nothing about the membership fee, so a Core cart was quoted 1'890 (+
 * add-ons) while `useBloodCheckout` sent the bundle's `expectedOneTimeCents` to
 * the edge function and the customer was charged 1'890 + 299 on top. Display and
 * charge have to come from the same model, and this is that one place.
 *
 * On a Core-shaped cart the card shows NO strikethrough and NO save badge:
 * joining costs more today (Sven/Laurent, 2026-08-06), so a saving is the wrong
 * claim. `deltaVsOneTimeCents` is what the card states instead.
 */
export function memberOptionPricing(opts: {
  cart: Product[];
  bundle: BundlePricing | null;
  bloodPricing: BloodMembershipPricing;
  plan: RecommendedPlan;
}): MemberOptionPricing {
  const { cart, bundle, bloodPricing, plan } = opts;
  const scanPackage = cart.find(isScanPackage);

  if (!bundle || !scanPackage) {
    return {
      oneTimeTotalCents: bloodPricing.fullTotalCents,
      memberTotalCents: bloodPricing.memberTotalCents,
      memberLines: bloodPricing.memberLines,
      strikeThroughCents:
        bloodPricing.savingsCents > 0 ? bloodPricing.fullTotalCents : null,
      savingsCents: bloodPricing.savingsCents,
      isBundle: false,
      addsBloodTest: false,
    };
  }

  const addsBloodTest = !absorbsMembershipFee(scanPackage);
  // The one-time card always quotes the cart itself, never the bundle baseline.
  const oneTimeTotalCents = bloodPricing.fullTotalCents;

  if (addsBloodTest) {
    return {
      oneTimeTotalCents,
      memberTotalCents: bundle.payNowCents,
      memberLines: bundle.lines,
      // No comparison: measured against "the scan plus a blood test bought
      // separately" the membership does save, but the customer never asked for
      // that basket, and the badge sat next to a smaller one-time price.
      strikeThroughCents: null,
      savingsCents: 0,
      isBundle: true,
      addsBloodTest: true,
    };
  }

  // Advanced / Pro absorb the fee, so the membership genuinely is cheaper and
  // keeps the save framing.
  const strikeThroughCents = bundleSeparateTotalCents(cart, plan);
  return {
    oneTimeTotalCents,
    memberTotalCents: bundle.payNowCents,
    memberLines: bundle.lines,
    strikeThroughCents,
    savingsCents: bundleSavingsCents(cart, bundle.payNowCents, plan),
    isBundle: true,
    addsBloodTest: false,
  };
}
